<template>
  <div>
    <b-button
      class="rounded-circle d-flex align-items-center justify-content-center p-0 querybuilder-action-button mx-1"
      :id="`popover-add-btn-${id}`"
      :variant="buttonVariantToShow"
    >
      <font-awesome-icon :icon="iconToShow"></font-awesome-icon>
    </b-button>
    <b-popover
      :target="`popover-add-btn-${id}`"
      triggers="click"
      customClass="ml-3"
      boundary="viewport"
      placement="right"
      @hidden="onHiddenPopover"
      @shown="onShownPopover"
    >
      <template #title>Agrega una condicion</template>
      <div v-if="!needOperator">
        <LmTreeSelect
          placeholder="Condición"
          :options="conditionOptions"
          v-model="newNode"
        />
        <LmTextInput
          v-if="newNode && newNode.dataType === 'object'"
          placeholder="Propiedades"
          :disabled="!newNode"
          v-model="nestedCustomProperties"
        />
        <LmTreeSelect
          placeholder="Validación"
          :disabled="!newNode"
          :options="validationOptions"
          v-model="validationForNode"
        />
        <LmTreeSelect
          placeholder="Valor"
          :disabled="!newNode || !validationForNode"
          v-if="newNode && newNode.dataType === 'boolean'"
          :options="[
            {id: 1, name: 'Verdadero', value: true},
            {id: 2, name: 'Falso', value: false}
          ]"
          v-model="valueForNode"
        />
        <div v-else>
          <div v-if="variablesMenu" class="d-flex align-items-start flex-nowrap">
            <LmTreeSelect
              :disabled="!newNode || !validationForNode"
              placeholder="Variables"
              pathToGetOptions="/codificatorbuilder/codificators/619fcbb5b3f4f641f4d7c3d9"
              v-model="valueForNode"
            />
            <b-button class="variables-menu-btn" variant="info" @click="variablesMenu = !variablesMenu">Text</b-button>
          </div>
          <b-input-group class="mb-3" v-else>
            <b-form-input
              placeholder="Valor"
              :disabled="!newNode || !validationForNode"
              v-model="valueForNode"
            ></b-form-input>
            <b-input-group-append>
              <b-button variant="info" @click="variablesMenu = !variablesMenu">Vars</b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <LmTreeSelect
        v-else
        :options="operatorOptions"
        v-model="newNode"
      />
      <div class="d-flex">
        <LmButton
          class="mr-2"
          :disabled="disableAddNodeButton"
          size="sm"
          @click="onNodeAdd"
          :value="$t('add_condition')"
          buttonClass="btn-primary"
        />
        <LmButton
          :disabled="needOperator"
          size="sm"
          @click="onGroupAdd"
          :value="$t('add_group')"
          buttonClass="btn-primary"
        />
      </div>
    </b-popover>
  </div>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmTextInput from '@/components/form/elements/LmTextInput'

// Utils
import { nanoid } from 'nanoid'
import _ from 'lodash'

export default {
  components: {
    LmButton,
    LmTreeSelect,
    LmTextInput
  },
  props: {
    id: {
      type: [Number, String],
      default: nanoid(5)
    },
    nodes: Array,
    settings: Array
  },
  data () {
    return {
      newNode: null,
      validationForNode: null,
      valueForNode: null,
      nestedCustomProperties: null,
      variablesMenu: false,
      newNodeMenu: false
    }
  },
  computed: {
    iconToShow () {
      return this.newNodeMenu ? 'times' : 'plus'
    },
    buttonVariantToShow () {
      return this.newNodeMenu ? 'outline-secondary' : 'outline-primary'
    },
    disableAddNodeButton () {
      if ((!this.newNode || !this.validationForNode || !this.valueForNode) && !this.needOperator) {
        return true
      }
      return false
    },
    needOperator () {
      return !!(this.nodes.at(-1)?.type === 'expression' || this.nodes.at(-1)?.children)
    },
    operatorOptions () {
      return [
        {
          id: nanoid(5),
          name: 'O',
          propertyLabel: 'O',
          value: 'OR',
          type: 'operator'
        },
        {
          id: nanoid(5),
          name: 'Y',
          propertyLabel: 'Y',
          value: 'AND',
          type: 'operator'
        }
      ]
    },
    conditionOptions () {
      const settings = _.cloneDeep(this.settings)
      return this.generateConditionOptions(settings)
    },
    validationOptions () {
      return [
        {
          id: nanoid(5),
          name: 'ES',
          value: ':'
        },
        {
          id: nanoid(5),
          name: 'NO ES',
          value: 'NOT'
        }
      ]
    }
  },
  methods: {
    onGroupAdd () {
      this.$root.$emit('bv::hide::popover')
      const newGroup = {
        id: nanoid(5),
        children: []
      }
      this.$emit('group-add', { nodes: this.nodes, newGroup })
    },
    onNodeAdd () {
      this.$root.$emit('bv::hide::popover')
      const newNode = this.buildNode()
      this.$emit('node-add', { nodes: this.nodes, newNode })

      // Limpio el data
      this.newNode = null
      this.validationForNode = null
      this.valueForNode = null
      this.nestedCustomProperties = null
    },
    generateConditionOptions (data, prevPropLabel = '', prevProp = '') {
      let options = []
      let propLabel = prevPropLabel
      let prop = prevProp

      data.forEach(el => {
        const prevLabelToConcat = propLabel ? `${propLabel}.${el?.name}` : el?.description
        const prevPropToConcat = prop ? `${prop}.${el?.name}` : el?.name

        if (el.type === 'nested') {
          const properties = []
          for (const key in el.properties) {
            properties.push({
              description: key,
              name: key,
              type: el.properties[key].type,
              properties: el.properties[key].properties || null
            })
          }
          let nested = this.generateConditionOptions(properties, prevLabelToConcat, prevPropToConcat)
          options = options.concat(nested)
        } else {
          options.push({
            id: nanoid(5),
            name: prevLabelToConcat || el.description,
            propertyLabel: prevLabelToConcat || el.description,
            property: prevPropToConcat || el.name,
            type: 'expression',
            dataType: el.type,
            error: false,
            errorMessage: null
          })
        }
      })
      return options
    },
    buildNode () {
      const node = _.cloneDeep(this.newNode)

      if (this.validationForNode) {
        node.validationLabel = this.validationForNode.name
        node.validation = this.validationForNode.value
      }
      if (this.valueForNode) {
        if (typeof this.valueForNode === 'string') {
          node.valueLabel = this.valueForNode
          node.value = this.valueForNode
        } else {
          node.valueLabel = this.valueForNode.name
          node.value = this.valueForNode.value
        }
      }
      if (this.nestedCustomProperties) {
        node.property += '.' + this.nestedCustomProperties
      }

      node.id = nanoid(5)

      return node
    },
    onHiddenPopover () {
      this.newNodeMenu = false
      // Limpio el data
      this.newNode = null
      this.validationForNode = null
      this.valueForNode = null
      this.nestedCustomProperties = null
    },
    onShownPopover () {
      this.newNodeMenu = true
    }
  }
}
</script>

<style>
  .querybuilder-action-button {
    width: 25px;
    height: 25px;
  }
  h3.popover-header {
    margin: 0;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
  .variables-menu-btn {
    height: 38px;
    border-radius: 0 .25rem .25rem 0;
  }
</style>
